import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Copy, Check, Loader2, Phone, MessageCircle } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { BookingForm } from "./BookingForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "savvmedia.info@gmail.com";
const PHONE_DISPLAY = "(417) 590-4345";
const PHONE_TEL = "tel:+14175904345";
const PHONE_SMS = "sms:+14175904345";

export const inputCls =
    "w-full bg-[#161616] border border-[#262626] rounded-2xl px-5 py-3.5 text-sm text-[#F1F1EF] placeholder:text-[#A9A6A0]/60 focus:border-[#D3A0A7] focus:ring-2 focus:ring-[#C9DDEE]/40 transition-colors duration-300";

const pill = (active) =>
    `px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors duration-300 ${
        active
            ? "bg-[#D3A0A7] border-[#D3A0A7] text-[#0C0C0C]"
            : "border-[#F1F1EF]/25 text-[#F1F1EF] hover:border-[#D3A0A7] hover:text-[#D3A0A7]"
    }`;

export const Contact = () => {
    const [mode, setMode] = useState("enquiry");
    const [form, setForm] = useState({
        name: "",
        email: "",
        business: "",
        service: "",
        message: "",
    });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const onBookCall = () => {
            setSent(false);
            setMode("book");
            toast.success("Pick a day and time that suits you ♡");
        };
        window.addEventListener("savv:book-call", onBookCall);
        return () => window.removeEventListener("savv:book-call", onBookCall);
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(EMAIL);
            setCopied(true);
            toast.success("Email copied — talk soon ♡");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Couldn't copy — it's " + EMAIL);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            setSent(true);
            toast.success("Enquiry sent — Tabbie will be in touch soon ♡");
        } catch {
            toast.error("Something went wrong — please email us instead.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-20 md:py-32 px-5 sm:px-8 lg:px-12"
        >
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                <div className="lg:col-span-5">
                    <Reveal>
                        <Eyebrow>Say hello.</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-4 font-serif-ed text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-[#F1F1EF]">
                            Tell us a bit about{" "}
                            <span className="italic text-[#D3A0A7]">
                                your business.
                            </span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-6 max-w-md text-base md:text-lg leading-relaxed text-[#A9A6A0]">
                            Let&apos;s turn your next idea into something people
                            remember.
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <div className="mt-9 flex flex-wrap items-center gap-3">
                            <a
                                href={`mailto:${EMAIL}`}
                                data-testid="contact-email-link"
                                className="group inline-flex items-center gap-2 bg-[#0C0C0C] text-[#F1F1EF] font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-[#D3A0A7] hover:text-[#0C0C0C] transition-colors duration-300"
                            >
                                Email Us
                                <ArrowRight
                                    size={16}
                                    className="transition-transform duration-300 group-hover:translate-x-1"
                                />
                            </a>
                            <button
                                onClick={copyEmail}
                                data-testid="contact-copy-email-btn"
                                className="inline-flex items-center gap-2 border border-[#F1F1EF]/25 text-[#F1F1EF] font-semibold text-sm px-6 py-3.5 rounded-full hover:border-[#D3A0A7] hover:text-[#D3A0A7] transition-colors duration-300"
                            >
                                {copied ? <Check size={16} /> : <Copy size={16} />}
                                {copied ? "Copied" : EMAIL}
                            </button>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-3">
                            <a
                                href={PHONE_TEL}
                                data-testid="contact-call-btn"
                                className="inline-flex items-center gap-2 bg-[#262626] text-[#F1F1EF] font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-[#0C0C0C] transition-colors duration-300"
                            >
                                <Phone size={16} />
                                Call {PHONE_DISPLAY}
                            </a>
                            <a
                                href={PHONE_SMS}
                                data-testid="contact-text-btn"
                                className="inline-flex items-center gap-2 border border-[#F1F1EF]/25 text-[#F1F1EF] font-semibold text-sm px-6 py-3.5 rounded-full hover:border-[#D3A0A7] hover:text-[#D3A0A7] transition-colors duration-300"
                            >
                                <MessageCircle size={16} />
                                Text Us
                            </a>
                            <span className="font-hand text-xl text-[#D3A0A7] rotate-[-2deg] px-1">
                                call or text anytime ♡
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.4}>
                        <p className="mt-10 font-hand text-3xl text-[#D3A0A7] rotate-[-2deg]">
                            can&apos;t wait to hear from you ♡
                        </p>
                    </Reveal>
                </div>

                <div className="lg:col-span-7">
                    <Reveal delay={0.15}>
                        <div className="flex flex-wrap gap-2 mb-5">
                            <button
                                data-testid="contact-mode-enquiry"
                                onClick={() => setMode("enquiry")}
                                className={pill(mode === "enquiry")}
                            >
                                Send an Enquiry
                            </button>
                            <button
                                data-testid="contact-mode-book"
                                onClick={() => setMode("book")}
                                className={pill(mode === "book")}
                            >
                                Book a Call ♡
                            </button>
                        </div>
                        {mode === "book" ? (
                            <BookingForm />
                        ) : sent ? (
                            <div
                                data-testid="enquiry-success"
                                className="bg-[#141414] rounded-[2rem] border border-[#CFCCC6]/50 p-10 md:p-14 text-center"
                            >
                                <p className="font-hand text-4xl text-[#F1F1EF]">
                                    thank you ♡
                                </p>
                                <h3 className="mt-4 font-serif-ed text-3xl tracking-tight text-[#F1F1EF]">
                                    Your enquiry is on its way.
                                </h3>
                                <p className="mt-3 text-[#A9A6A0]">
                                    Tabbie will get back to you soon. Prefer
                                    email?{" "}
                                    <a
                                        href={`mailto:${EMAIL}`}
                                        className="text-[#D3A0A7] font-semibold link-underline"
                                    >
                                        {EMAIL}
                                    </a>
                                </p>
                            </div>
                        ) : (
                            <form
                                onSubmit={submit}
                                data-testid="enquiry-form"
                                className="bg-[#101010] rounded-[2rem] border border-[#262626] shadow-[0_25px_60px_-25px_rgba(0,0,0,0.6)] p-7 md:p-10 space-y-4"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <input
                                        required
                                        data-testid="enquiry-name-input"
                                        placeholder="Your name"
                                        value={form.name}
                                        onChange={set("name")}
                                        className={inputCls}
                                    />
                                    <input
                                        required
                                        type="email"
                                        data-testid="enquiry-email-input"
                                        placeholder="Email address"
                                        value={form.email}
                                        onChange={set("email")}
                                        className={inputCls}
                                    />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <input
                                        data-testid="enquiry-business-input"
                                        placeholder="Business name (optional)"
                                        value={form.business}
                                        onChange={set("business")}
                                        className={inputCls}
                                    />
                                    <select
                                        data-testid="enquiry-service-select"
                                        value={form.service}
                                        onChange={set("service")}
                                        className={`${inputCls} ${form.service ? "" : "text-[#A9A6A0]/60"}`}
                                    >
                                        <option value="">What do you need?</option>
                                        <option>Branding</option>
                                        <option>Graphic Design</option>
                                        <option>Social Media</option>
                                        <option>Marketing</option>
                                        <option>Web Design</option>
                                        <option>Content</option>
                                        <option>A bit of everything</option>
                                    </select>
                                </div>
                                <textarea
                                    required
                                    rows={5}
                                    data-testid="enquiry-message-input"
                                    placeholder="Tell us about your project — what are you dreaming up?"
                                    value={form.message}
                                    onChange={set("message")}
                                    className={`${inputCls} resize-none`}
                                />
                                <button
                                    type="submit"
                                    disabled={sending}
                                    data-testid="enquiry-submit-btn"
                                    className="group w-full inline-flex items-center justify-center gap-2 bg-[#D3A0A7] text-[#0C0C0C] font-semibold text-sm px-7 py-4 rounded-full hover:bg-[#0C0C0C] hover:text-[#F1F1EF] transition-colors duration-300 disabled:opacity-60"
                                >
                                    {sending ? (
                                        <>
                                            Sending <Loader2 size={16} className="animate-spin" />
                                        </>
                                    ) : (
                                        <>
                                            Send My Enquiry
                                            <ArrowRight
                                                size={16}
                                                className="transition-transform duration-300 group-hover:translate-x-1"
                                            />
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
